import { addDays, addMinutes, parseISO } from "date-fns";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { Form } from "@/components/Form/Form";
import { FormDatePicker } from "@/components/Form/FormDatePicker";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Stack } from "@/design-system/Stack";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { ticketMutations } from "@/queries/tickets/mutations";
import { useTranslation } from "@/translations";

const FORM_ID = "ticket-reminder-modal-form";

type TicketReminderModalProps = ModalBaseProps & {
  ticketId: string;
  reminderDate?: string;
  daysToAutoClose: number;
};

interface FormValues {
  reminderDate: Date;
}

export function TicketReminderModal({
  ticketId,
  isOpened,
  onOpenChange,
  reminderDate,
  daysToAutoClose,
}: TicketReminderModalProps): React.ReactNode {
  const clockDate = useCurrentDate();
  const createReminder = ticketMutations.useCreateReminder(ticketId);
  const deleteReminderMutation = ticketMutations.useDeleteReminder(ticketId);

  const isLoading = createReminder.isPending || deleteReminderMutation.isPending;

  const { t } = useTranslation();
  const [defaultReminderDate] = useState(() => (reminderDate ? parseISO(reminderDate) : addDays(new Date(), 1)));

  const form = useForm<FormValues>({
    defaultValues: {
      reminderDate: defaultReminderDate,
    },
  });

  const minScheduled = addMinutes(clockDate, 5);

  const isEdit = !!reminderDate;

  const handleSave = async () => {
    await createReminder.mutateAsync({
      reminderDate: form.getValues("reminderDate").toISOString(),
    });

    onOpenChange(false);
  };

  const handleDelete = async () => {
    await deleteReminderMutation.mutateAsync();

    onOpenChange(false);
  };

  const deleteReminder = deleteReminderMutation.mutate;
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (reminderDate && parseISO(reminderDate) < new Date()) {
        deleteReminder();
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [ticketId, reminderDate, deleteReminder]);

  return (
    <Modal.Root
      title={
        isEdit
          ? t("page.tickets.details.reminder.modal.edit.title")
          : t("page.tickets.details.reminder.modal.create.title")
      }
      description={t("page.tickets.details.reminder.modal.description")}
      size="sm"
      shouldCloseOnClickOutside={false}
      isNested
      {...{ isOpened, onOpenChange }}
      data-testid="ticket-reminder-modal"
    >
      <Modal.Body>
        <Stack align="center" gap="sm">
          <Form.Root id={FORM_ID} fullWidth formMethods={form} onSubmit={handleSave}>
            <FormField label={t("page.tickets.details.reminder.modal.datepicker.name")} required>
              <FormDatePicker<FormValues>
                name="reminderDate"
                type="datetime"
                min={minScheduled}
                rules={{
                  validate: {
                    laterThanMin: (date) => {
                      if (date == null) {
                        return undefined;
                      }

                      return date < minScheduled
                        ? t("page.tickets.details.reminder.modal.datepicker.error.must-be-in-future")
                        : undefined;
                    },
                    earlierThanAutoClose: (date) => {
                      if (date == null || daysToAutoClose == 0) {
                        return undefined;
                      }

                      return date > addDays(new Date(), daysToAutoClose)
                        ? t("page.tickets.details.reminder.modal.datepicker.error.must-be-before-auto-close")
                        : undefined;
                    },
                  },
                  required: t("components.form.error.required", {
                    inputName: t("page.tickets.details.reminder.modal.datepicker.name"),
                  }),
                }}
              />
            </FormField>
          </Form.Root>
        </Stack>
      </Modal.Body>
      <Modal.Actions>
        {isEdit ? (
          <>
            <Button variant="outline" tone="error" onClick={handleDelete} disabled={isLoading}>
              {t("page.tickets.details.reminder.modal.button.delete")}
            </Button>
            <Button type="submit" form={FORM_ID} isLoading={isLoading}>
              {t("page.tickets.details.reminder.modal.button.edit")}
            </Button>
          </>
        ) : (
          <>
            <Modal.Close>
              <Button variant="outline" tone="primary" disabled={isLoading}>
                {t("common.action.cancel")}
              </Button>
            </Modal.Close>
            <Button variant="filled" type="submit" form={FORM_ID} isLoading={isLoading}>
              {t("page.tickets.details.reminder.modal.button.create")}
            </Button>
          </>
        )}
      </Modal.Actions>
    </Modal.Root>
  );
}
